import { Dialog } from '@headlessui/react';
import { useEffect, useRef } from 'react';
import enedaLogo from '@/assets/images/NavBar/eneda-logo.png';
import cybernestLogo from '@/assets/images/Hero/cybernest.png';
import flowLogo from '@/assets/images/Hero/flow.png';
import type React from 'react';

type TrackNumberModalProps = {
  open: boolean;
  onClose: () => void;
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  onTrack?: () => void; // ✅ allow parent to handle routing
};

const TrackNumberModal = ({
  open,
  onClose,
  value,
  setValue,
  onTrack,
}: TrackNumberModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [open]);

  // Fallback local navigation only if parent didn't supply onTrack
  const fallbackTrack = () => {
    if (value?.trim()) {
      const base = 'https://eneda-ph.com/track';
      const encoded = encodeURIComponent(value.trim());
      window.location.href = `${base}?tn=${encoded}`;
    }
  };

  const handleTrackClick = () => {
    if (onTrack) onTrack();
    else fallbackTrack();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') handleTrackClick();
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      {/* Modal panel */}
      <div className="fixed inset-0 flex items-center justify-center p-3">
        <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 text-center shadow-xl sm:p-8">
          {/* Logo */}
          <img
            src={enedaLogo}
            alt="ENEDA Logo"
            className="mx-auto h-12 w-auto sm:h-14"
          />

          {/* Title */}
          <Dialog.Title className="mt-4 text-base font-bold text-primary sm:text-xl">
            Tracking Number
          </Dialog.Title>
          <p className="mt-2 text-xs text-gray-700 sm:text-sm">
            Track the status of your application. Stay informed as your
            submission progresses through review and evaluation.
          </p>

          {/* Input */}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter your tracking number"
            aria-label="Tracking number"
            className="mt-5 w-full rounded-md border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />

          {/* Action buttons */}
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-full border border-primary bg-white px-4 py-2 text-sm font-semibold text-primary transition hover:bg-gray-100 sm:w-32"
            >
              Go Back
            </button>
            <button
              type="button"
              onClick={handleTrackClick}
              className="w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 sm:w-32"
            >
              Track
            </button>
          </div>

          {/* Footer logos */}
          <div className="mt-6 flex flex-col items-center text-xs text-gray-500 sm:text-sm">
            <span className="mb-3">Powered by</span>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <img
                src={cybernestLogo}
                alt="Cybernest Solutions"
                className="h-8 border-r-2 pr-3 sm:h-9"
              />
              <img src={flowLogo} alt="FLOW" className="h-5 sm:h-6" />
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default TrackNumberModal;
